import type { CSSProperties } from 'react';
import { useTheme } from 'next-themes';
import { useMounted } from '@/hooks/mounted.hook';
import { cn } from '@/utils/classnames';
import { MoonIcon, SunIcon } from './icon';

interface ThemeToggleProps {
  className?: string;
  size?: number;
}

export default function ThemeToggle({ className, size = 44 }: ThemeToggleProps) {
  const mounted = useMounted();
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  const scale = size / 44;
  const toggleStyle = {
    width: `${74 * scale}px`,
    height: `${36 * scale}px`,
  } satisfies CSSProperties;
  const knobStyle = {
    '--theme-toggle-inset': `${4 * scale}px`,
    '--theme-toggle-knob-size': `${28 * scale}px`,
    '--theme-toggle-active-width': `${46 * scale}px`,
    '--theme-toggle-checked-x': `${38 * scale}px`,
    '--theme-toggle-checked-active-x': `${20 * scale}px`,
  } as CSSProperties;

  if (!mounted) {
    return (
      <div
        className={cn('shrink-0 rounded-full bg-[#EBF7FC]', className)}
        style={toggleStyle}
        suppressHydrationWarning
      />
    );
  }

  return (
    <label
      className={cn(
        'theme-toggle-button relative block shrink-0 cursor-pointer overflow-hidden rounded-full',
        className,
      )}
      style={toggleStyle}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      suppressHydrationWarning
    >
      <input
        className="peer absolute inset-0 z-30 h-full w-full cursor-pointer opacity-0"
        type="checkbox"
        checked={isDark}
        onChange={() => setTheme(isDark ? 'light' : 'dark')}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      />

      <span className="absolute inset-0 z-10 rounded-full bg-[#F4F7FE] transition-colors duration-300 ease-in-out peer-checked:bg-[#212B3B]" />

      <span
        className={cn(
          'absolute left-[var(--theme-toggle-inset)] top-[var(--theme-toggle-inset)] z-20 flex h-[var(--theme-toggle-knob-size)] w-[var(--theme-toggle-knob-size)] items-center justify-center rounded-full bg-[#FFFFFF] text-[0.9rem] font-bold leading-none text-white transition-all duration-300 ease-in-out shadow-[0_2.67px_5.33px_0_rgba(0,0,0,0.2),inset_0_-1.33px_1.33px_0_rgba(0,0,0,0.1),inset_0_2.67px_2.67px_0_rgba(255,255,255,1)]',
          'peer-active:w-[var(--theme-toggle-active-width)] peer-active:rounded-full',
          'peer-checked:translate-x-[var(--theme-toggle-checked-x)] peer-checked:bg-[#6E7C95] peer-checked:shadow-[0px_2.67px_5.33px_0px_#7988A4,inset_0px_-1.33px_1.4px_0px_#6E7C9599,inset_0px_2.67px_2.67px_0px_#6E7C95]',
          'peer-checked:peer-active:translate-x-[var(--theme-toggle-checked-active-x)]',
        )}
        style={knobStyle}
      >
        {isDark ? <MoonIcon /> : <SunIcon />}
      </span>
    </label>
  );
}